import { Directive, inject, TemplateRef } from '@angular/core'
import { CertificateHolderRow } from '@features/certificate/utils/certificate-holder-search.util'

/**
 * Context handed to every action-slot template rendered by the
 * {@link CertificateHolderEntityManagerComponent}. The flattened row that the action buttons act
 * on is exposed both as the implicit value (`let-row`) and as the named `row` property so
 * consumers can pick whichever binding style they prefer.
 */
export interface CertificateHolderRowActionContext {
  $implicit: CertificateHolderRow
  row: CertificateHolderRow
}

/**
 * Base class for the action-slot marker directives. It captures the decorated `<ng-template>` as a
 * strongly typed {@link TemplateRef} and provides the `ngTemplateContextGuard` so that the implicit
 * template variables (`let-row`) are correctly typed as {@link CertificateHolderRow} on the
 * consumer side.
 */
@Directive()
export abstract class CertificateHolderRowActionDirective {
  public readonly templateRef = inject<TemplateRef<CertificateHolderRowActionContext>>(TemplateRef)

  public static ngTemplateContextGuard(
    _directive: CertificateHolderRowActionDirective,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- referenced by the type predicate
    _context: unknown
  ): _context is CertificateHolderRowActionContext {
    return true
  }
}

/** Action buttons for a holder row inside the already-added ("display") table. */
@Directive({
  selector: 'ng-template[albertHolderDisplayActions]',
  standalone: true
})
export class HolderDisplayActionsDirective extends CertificateHolderRowActionDirective {}

/** Action buttons for an address row inside the already-added ("display") table. */
@Directive({
  selector: 'ng-template[albertAddressDisplayActions]',
  standalone: true
})
export class AddressDisplayActionsDirective extends CertificateHolderRowActionDirective {}

/** Action buttons for a holder row inside the search-results table. */
@Directive({
  selector: 'ng-template[albertHolderSearchActions]',
  standalone: true
})
export class HolderSearchActionsDirective extends CertificateHolderRowActionDirective {}

/** Action buttons for an address row inside the search-results table. */
@Directive({
  selector: 'ng-template[albertAddressSearchActions]',
  standalone: true
})
export class AddressSearchActionsDirective extends CertificateHolderRowActionDirective {}
